import {
  IImmediatePaymentDetails,
  IScheduleOneTimePaymentDetails,
  IResponseAutopayDetails,
  IResponseUpdateAutopayDetails,
  IRecentPaymentProps,
} from '../../store/interface';
import {
  API_ROUTES_FOR_ACTIVE_MEMBER,
  API_ROUTES_FOR_BINDER, API_ROUTES_FOR_BROKER,
} from '../constants';
import { HttpResponsePromise } from '../models';
import {
  IImmediatePaymentDetailsRequestBody,
  IPaymentMethod,
  ISchedulePayments,
  IScheduledPaymentDetails,
  IAutopayDetails,
  IUpdateScheduledPaymentDetails,
  IUpdateAutopayRequestBody,
  IBinderSubmitPaymentDetails,
  IManageAutopayDetails,
  IBinderSchedulePaymentDetails,
  IBinderImmediatePaymentDetails,
  IBinderUpdateSchedulePaymentDetails,
  IBinderAutoPaymentForPayLater,
  ISessionTokenRequestPayload,
  IRecentPaymentDetailsRequestBody,
} from '../models/PaymentApiModels';
import utils, {
  IAPIHeaders,
  ICancelAutoPaymentHeader,
  ICancelSchedulePaymentHeader,
  IActiveMemberAPIHeaders,
  IGetAutoPayments,
} from '../utils';
import { USER_TYPES } from '../../constants/common';

const payment = () => {
  const { fetch, getUserSpecificAPIRoute } = utils;
  return {
    getSessionToken: (
      userTypeCode: number,
      headers: IAPIHeaders,
      data: ISessionTokenRequestPayload,
      isCABinder?: boolean,
    ) => {
      return fetch(
        `${
          getUserSpecificAPIRoute(userTypeCode, isCABinder).session
            .getSessionToken
        }`,
        {
          method: 'POST',
          data,
          headers,
        },
      );
    },
    submitImmediatePayment: (
      data: IImmediatePaymentDetailsRequestBody,
      userTypeCode: number,
      headers: IAPIHeaders,
    ): HttpResponsePromise<IImmediatePaymentDetails> => {
      return fetch(
        `${
          getUserSpecificAPIRoute(userTypeCode).payment.submitImmeditatePayment
        }`,
        {
          method: 'POST',
          data,
          headers,
        },
      );
    },

    getRecentPayment: (
      data: IRecentPaymentDetailsRequestBody,
      headers: IAPIHeaders,
      userTypeCode: number,
    ): HttpResponsePromise<IRecentPaymentProps[]> => {
      return fetch(
        `${getUserSpecificAPIRoute(userTypeCode).payment.getRecentPayment}`,
        {
          method: 'POST',
          data,
          headers,
        },
      );
    },

    submitGovernmentImmediatePayment: (
      data: IImmediatePaymentDetailsRequestBody,
      userTypeCode: number,
      headers: IAPIHeaders,
    ): HttpResponsePromise<IImmediatePaymentDetails> => {
      return fetch(
        `${
          getUserSpecificAPIRoute(userTypeCode).payment
            .submitGovernmentImmeditatePayment
        }`,
        {
          method: 'POST',
          data,
          headers,
        },
      );
    },

    submitScheduledOneTimePayment: (
      data: IScheduledPaymentDetails,
      userTypeCode: number,
      headers: IAPIHeaders,
    ): HttpResponsePromise<IScheduleOneTimePaymentDetails> => {
      return utils.fetch(
        `${
          getUserSpecificAPIRoute(userTypeCode).payment.scheduledOneTimePayment
        }`,
        {
          method: 'POST',
          data,
          headers,
        },
      );
    },
    /* used for binder and active schedule call*/
    getScheduledOneTimePayment: (
      data: ISchedulePayments,
      headers: IAPIHeaders,
      userType: number,
      isCABinder?: boolean,
    ) => {
      return utils.fetch(
        `${
          getUserSpecificAPIRoute(userType, isCABinder).payment
            .getScheduledOneTimePayment
        }`,
        {
          method: 'POST',
          data,
          headers,
        },
      );
    },
    /*used for both active and binder pay later*/
    cancelScheduledOneTimePayment: (
      data: ISchedulePayments,
      userTypeCode: number,
      headers: ICancelSchedulePaymentHeader,
      isCABinder?: boolean,
    ) => {
      return utils.fetch(
        `${
          getUserSpecificAPIRoute(userTypeCode, isCABinder).payment
            .deleteScheduledOneTimePayment
        }`,
        {
          method: 'POST',
          data,
          headers,
        },
      );
    },
    updateScheduledOneTimePayment: (
      data: IUpdateScheduledPaymentDetails,
      headers: ICancelSchedulePaymentHeader,
      userTypeCode: number,
    ): HttpResponsePromise<Partial<IScheduleOneTimePaymentDetails>> => {
      return utils.fetch(
        `${
          getUserSpecificAPIRoute(userTypeCode).payment.updateScheduledOneTimePayment
        }`,
        {
          method: 'POST',
          data,
          headers,
        },
      );
    },
    updatebinderScheduledOneTimePayment: (
      data: IBinderUpdateSchedulePaymentDetails,
      headers: IAPIHeaders,
    ) => {
      return utils.fetch(
        `${API_ROUTES_FOR_BINDER.payment.updateScheduledOneTimePayment}`,
        {
          method: 'POST',
          data,
          headers,
        },
      );
    },
    getPaymentMethod: (
      userTypeCode: number,
      data: IPaymentMethod,
      headers: IActiveMemberAPIHeaders,
    ) => {
      const { stateCode, exchangeIndicator } = data;
      return fetch(
        `${
          getUserSpecificAPIRoute(userTypeCode).payment.paymentMethod
        }?stateCode=${stateCode}&exchangeIndicator=${exchangeIndicator}`,
        {
          method: 'GET',
          data,
          headers,
        },
      );
    },
    getBinderPaymentMethod: (
      userTypeCode: number,
      data: IPaymentMethod,
      headers: IAPIHeaders,
      isCABinder?: boolean,
    ) => {
      const { stateCode, exchangeIndicator } = data;
      return fetch(
        `${
          getUserSpecificAPIRoute(userTypeCode, isCABinder).payment
            .paymentMethod
        }?stateCode=${stateCode}&exchangeIndicator=${exchangeIndicator}`,
        {
          method: 'GET',
          data,
          headers,
        },
      );
    },
    submitAutoPayment: (
      data: IAutopayDetails,
      userTypeCode: number,
      headers: IAPIHeaders,
    ): HttpResponsePromise<IResponseAutopayDetails> => {
      return utils.fetch(
        `${getUserSpecificAPIRoute(userTypeCode).autoPay.autoPayment}`,
        {
          method: 'POST',
          data,
          headers,
        },
      );
    },
    getAutoPayments: (
      data: IAutopayDetails,
      userTypeCode: number,
      headers: IAPIHeaders,
    ) => {
      return utils.fetch(
        `${getUserSpecificAPIRoute(userTypeCode).autoPay.getAutoPayment}`,
        {
          method: 'POST',
          data,
          headers: headers,
        },
      );
    },
    getAutoPaymentsBinder: (headers: IAPIHeaders, body: IGetAutoPayments) => {
      return utils.fetch(`${API_ROUTES_FOR_BINDER.autopayment.getAutopay}`, {
        method: 'POST',
        data: body,
        headers: headers,
      });
    },

    manageAutoPayment: (
      data: IManageAutopayDetails,
      headers: IAPIHeaders,
    ): HttpResponsePromise<IResponseUpdateAutopayDetails> => {
      return utils.fetch(`${API_ROUTES_FOR_BINDER.autopayment.manageAutopay}`, {
        method: 'POST',
        data,
        headers,
      });
    },
            updateAutoPayment: (
      data: IUpdateAutopayRequestBody,
      userTypeCode: number,
      headers: IAPIHeaders,
    ): HttpResponsePromise<IResponseUpdateAutopayDetails> => {
      const apiRoute = userTypeCode === USER_TYPES.BROKER
        ? API_ROUTES_FOR_BROKER.autoPay.updateAutoPayment
        : API_ROUTES_FOR_ACTIVE_MEMBER.autoPay.updateAutoPayment;
      return utils.fetch(apiRoute,
        {
          method: 'POST',
          data,
          headers,
        },
      );
    },
    cancelAutoPayment: (
      data: IAutopayDetails,
      headers: ICancelAutoPaymentHeader,
    ) => {
      return utils.fetch(
        `${API_ROUTES_FOR_ACTIVE_MEMBER.autoPay.deleteAutoPayment}`,
        {
          method: 'POST',
          data,
          headers,
        },
      );
    },
    submitBinderPayment: (
      data: IBinderSubmitPaymentDetails,
      headers: IAPIHeaders,
    ) => {
      return utils.fetch(`${API_ROUTES_FOR_BINDER.payment.submitPayment}`, {
        method: 'POST',
        data,
        headers,
      });
    },
    scheduleBinderPayment: (
      data: IBinderSchedulePaymentDetails,
      headers: IAPIHeaders,
    ) => {
      return utils.fetch(`${API_ROUTES_FOR_BINDER.payment.schedulePayment}`, {
        method: 'POST',
        data,
        headers,
      });
    },
    immediateBinderPayment: (
      data: IBinderImmediatePaymentDetails,
      headers: IAPIHeaders,
    ) => {
      return utils.fetch(`${API_ROUTES_FOR_BINDER.payment.immediatePayment}`, {
        method: 'POST',
        data,
        headers,
      });
    },
    createAutoPaymentForBinderPayLater: (
      data: IBinderAutoPaymentForPayLater,
      headers: IAPIHeaders,
    ) => {
      return utils.fetch(`${API_ROUTES_FOR_BINDER.autopayment.createAutoPay}`, {
        method: 'POST',
        data,
        headers,
      });
    },

    getScheduledOneTimePaymentIndicator: (
      data: ISchedulePayments,
      headers: IAPIHeaders,
      userType: number,
    ) => {
      return utils.fetch(
        `${
          getUserSpecificAPIRoute(userType).payment.getScheduledPaymentIndicator
        }`,
        {
          method: 'POST',
          data,
          headers,
        },
      );
    },
  };
};

export default payment;
